import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { api } from "@/lib/api";
import { useEffect } from "react";
import { analytics } from "@/lib/analytics";

interface Subscription {
  plan: string;
  planName: string;
  domainLimit: number;
  messageLimit: number;
}

export function CheckoutSuccessPage() {
  const { currentOrganization, setOrganizations } = useAuthStore();

  const { data: subscription } = useQuery<Subscription>({
    queryKey: ["subscription", currentOrganization?.id],
    queryFn: () => api.billing.getSubscription(currentOrganization!.id) as Promise<Subscription>,
    enabled: !!currentOrganization?.id,
  });

  // Refresh org data to get updated plan limits
  useEffect(() => {
    if (currentOrganization) {
      api.organizations.list().then((orgs) => {
        setOrganizations(orgs);
      });
    }
  }, [currentOrganization, setOrganizations]);

  useEffect(() => {
    if (subscription?.planName) {
      analytics.subscriptionStarted(subscription.planName);
    }
  }, [subscription?.planName]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center">
      <div className="max-w-lg w-full text-center">
        {/* Success icon with animation */}
        <div className="relative mx-auto w-24 h-24 mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full animate-pulse opacity-20" />
          <div className="absolute inset-2 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          {/* Confetti dots */}
          <div className="absolute -top-2 -left-2 w-3 h-3 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <div className="absolute -top-1 right-0 w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <div className="absolute top-4 -right-3 w-3 h-3 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "100ms" }} />
          <div className="absolute bottom-2 -right-2 w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "200ms" }} />
        </div>

        <h1 className="text-3xl font-bold text-foreground">You're all set!</h1>
        <p className="mt-2 text-lg text-muted-foreground">
          Welcome to GetSent {subscription?.planName && <span className="font-semibold text-foreground">{subscription.planName}</span>}
        </p>

        {/* Plan details card */}
        {subscription && (
          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 border border-indigo-100 dark:border-indigo-900">
            <h2 className="font-semibold text-lg">Your plan includes</h2>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-white dark:bg-gray-900 shadow-sm">
                <div className="text-3xl font-bold text-indigo-600">
                  {subscription.domainLimit === -1 ? "Unlimited" : subscription.domainLimit}
                </div>
                <div className="text-sm text-muted-foreground">Domains</div>
              </div>
              <div className="p-4 rounded-xl bg-white dark:bg-gray-900 shadow-sm">
                <div className="text-3xl font-bold text-indigo-600">
                  {subscription.messageLimit === -1 ? "Unlimited" : (subscription.messageLimit / 1000).toFixed(0) + "k"}
                </div>
                <div className="text-sm text-muted-foreground">Messages/month</div>
              </div>
            </div>
          </div>
        )}

        {/* Next steps */}
        <div className="mt-8 space-y-3">
          <h3 className="font-medium text-muted-foreground">Next steps</h3>
          <div className="flex flex-col gap-2">
            <Link
              to="/domains"
              className="flex items-center justify-between p-4 rounded-xl bg-card border hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-medium">Add your domains</div>
                  <div className="text-sm text-muted-foreground">Start monitoring DMARC reports</div>
                </div>
              </div>
              <svg className="w-5 h-5 text-muted-foreground group-hover:text-indigo-500 transition-colors" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </Link>

            <Link
              to="/team"
              className="flex items-center justify-between p-4 rounded-xl bg-card border hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-medium">Invite your team</div>
                  <div className="text-sm text-muted-foreground">Collaborate with colleagues</div>
                </div>
              </div>
              <svg className="w-5 h-5 text-muted-foreground group-hover:text-indigo-500 transition-colors" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Go to dashboard button */}
        <div className="mt-8">
          <Link
            to="/dashboard"
            className="btn btn-primary btn-lg w-full justify-center"
          >
            Go to Dashboard
          </Link>
        </div>

        <p className="mt-6 text-sm text-muted-foreground">
          Questions? Email us at{" "}
          <a href="mailto:support@getsent.dev" className="text-indigo-600 hover:underline">
            support@getsent.dev
          </a>
        </p>
      </div>
    </div>
  );
}
